import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories, navLinks } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm"
          : "bg-background-50/85 backdrop-blur-md border-b border-background-200/40"
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-4 md:px-6 py-3.5">
        {/* Logo */}
        <Link
          to="/"
          className="text-xl md:text-2xl font-bold font-heading tracking-tight whitespace-nowrap text-foreground-900 hover:text-primary-600 transition-colors"
          onClick={closeAll}
        >
          acbuyspreadsheet
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="text-sm font-semibold text-foreground-700 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}

          {/* Categories Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground-700 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Categories
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-60 bg-background-50 rounded-xl border border-background-200/80 py-3 max-h-80 overflow-y-auto shadow-lg">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                  >
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                    />
                    <span className="whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Shopping Guide CTA */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 rounded-full text-sm font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-900 cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`text-2xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              onClick={closeAll}
              className="flex items-center gap-3 text-sm font-semibold text-foreground-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg py-3 px-3 transition-colors"
            >
              {link.label === "Home" ? (
                <i className="ri-home-4-line text-lg w-6 h-6 flex items-center justify-center"></i>
              ) : link.label === "Shopping Guide" ? (
                <i className="ri-shopping-bag-3-line text-lg w-6 h-6 flex items-center justify-center"></i>
              ) : (
                <i className="ri-book-open-line text-lg w-6 h-6 flex items-center justify-center"></i>
              )}
              {link.label}
            </Link>
          ))}

          {/* Mobile Categories Toggle */}
          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between w-full text-sm font-semibold text-foreground-700 hover:bg-primary-50 rounded-lg py-3 px-3 transition-colors cursor-pointer mt-1 border-t border-background-200/50 pt-4"
          >
            <span className="flex items-center gap-3">
              <i className="ri-grid-line text-lg w-6 h-6 flex items-center justify-center"></i>
              Categories
            </span>
            <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
          </button>

          {mobileCatOpen && (
            <div className="grid grid-cols-2 gap-2 px-3 pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeAll}
                  className="flex items-center gap-2.5 text-xs font-medium text-foreground-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg py-2.5 px-2 transition-colors"
                >
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    className="w-7 h-7 rounded object-cover flex-shrink-0"
                  />
                  <span className="leading-tight">{cat.name}</span>
                </a>
              ))}
            </div>
          )}

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            onClick={closeAll}
            className="mt-3 px-5 py-3 rounded-full text-sm font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors text-center whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-shopping-bag-line"></i>
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}